/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React, { useEffect, useRef } from "react";
import { Toast } from "@react/react-spectrum/Toast";
import classNames from "classnames";
import ResizeObserver from "resize-observer-polyfill";
import Styles from "./index.css";

const BannerNotification = ({
  overrideClassName,
  intl,
  msg,
  variant,
  closeNotification,
  index,
  actionLabel,
  onAction,
  closeOnAction,
  setNotificationBannerHeight,
  id,
  notificationBannerHeight,
  shouldUseMobileModernViewer,
  messageIcon,
  renderBannerBeforeDocView
}) => {
  const bannerRef = useRef();
  useEffect(() => {
    setNotificationBannerHeight(id, bannerRef?.current?.clientHeight);
    return () => setNotificationBannerHeight(id, 0);
  }, [id]);

  useEffect(() => {
    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => {
        setNotificationBannerHeight(id, bannerRef?.current?.clientHeight);
      });

      if (bannerRef?.current) {
        resizeObserver.observe(bannerRef.current);
      }
    }
    return () => {
      if (bannerRef?.current) {
        resizeObserver.unobserve(bannerRef.current);
      }
    };
  }, [id]);

  const wrapperClassName = renderBannerBeforeDocView ? Styles.NotificationBannerWrapper : Styles.Notification;
  const translateXOffset = renderBannerBeforeDocView ? "0" : "-50%";

  return (
    <span
      ref={bannerRef}
      className={wrapperClassName}
      data-testid="notification-banner"
      style={{ transform: `translateX(${translateXOffset}) translateY(${index * 16 + notificationBannerHeight}px)` }}
    >
      <Toast
        className={classNames(
          shouldUseMobileModernViewer ? Styles.NotificationToastMobile : Styles.NotificationBanner,
          overrideClassName
        )}
        variant={variant}
        closable
        closeOnAction={closeOnAction}
        onClose={closeNotification}
        onAction={onAction}
        actionLabel={actionLabel}
      >
        {messageIcon}
        {intl.formatMessage({ id: msg })}
      </Toast>
    </span>
  );
};

export default BannerNotification;
